import { memo, useMemo } from "react";
import { useAppSelector } from "../../store/hooks/hooks";
import { Trans } from "react-i18next";
import Input from "../../components/common/Input";
import DropdownMenu, { type IDDItem } from "../../components/common/DropdownMenu";

interface ToolInputRowProps {
	inputId: string;
	dropdownItems: IDDItem[];
	onInputChange: (inputName: string, value: number | null) => void;
	onSelectUnit: (inputId: string, toUnitId: string | null) => void;
}

export const InputRow: React.FC<ToolInputRowProps> = memo(({
	inputId,
	dropdownItems,
	onInputChange,
	onSelectUnit,
}) => {
	const input = useAppSelector(state =>
		state.pressureDropCalculator?.inputs.find(item => item.id === inputId)
	);

	if (!input) {
		return null;
	}

	const showHints = useAppSelector(state => state.settings.showHints);
	const tooltipContent = useMemo(() => {
		if (!showHints) {
			return undefined;
		}

		return (
			<Trans
				ns={'pressure_drop'}
				i18nKey={`form_field_titles.${inputId}`}
				components={{
					sub: <sub />,
					sup: <sup />,
				}}
			/>
		);
	}, [showHints, inputId]);

	const labelContent = useMemo(() => {
		return (
			<Trans
				ns={"pressure_drop"}
				i18nKey={`form_fields.${inputId}`}
				components={{
					sub: <sub />,
					sup: <sup />,
				}}
			/>
		);
	}, [inputId]);

	const onDDSelectItem = (selectedItemId: string | null) => {
		onSelectUnit(input.id, selectedItemId);
	}

	const value: number | null = useMemo(() => {
		if (input.currentValue == null)
			return null;

		if (input.currentUnitId != null && input.currentUnitId == input.userUnitId)
			return input.userValue;

		return parseFloat(input.currentValue.toFixed(4));
	}, [input]);

	return (
		<div className="pressure-drop__input-group">
			<label className="pressure-drop__input-label" htmlFor={input.id}>
				{labelContent}
			</label>
			<Input
				value={value}
				onChange={onInputChange}
				name={input.queryName}
				id={input.id}
				tooltip={tooltipContent}
				error={input.error}
				errorParams={input.errorParams}
			/>
			{input.unitCategoryId && input.currentUnitId && (
				<DropdownMenu
					inputId={input.id}
					items={dropdownItems}
					selectedItemId={input.currentUnitId}
					onSelect={onDDSelectItem}
				/>)
			}
		</div>
	);
});

InputRow.displayName = "PressureDropInputRow";
